import { Component, OnInit } from '@angular/core';
import { BeUtil, FeUtil } from '../../../../hezky-routes';
import { DateTime } from 'luxon';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { finalize } from 'rxjs';
import { NzSliderValue } from 'ng-zorro-antd/slider';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-aqi-heat-map',
  templateUrl: './aqi-heat-map.component.html',
})
export class AqiHeatMapComponent implements OnInit {
  loader = { aqiLoader: false };
  mapOptions: google.maps.MapOptions = {
    streetViewControl: false,
    fullscreenControl: true,
    fullscreenControlOptions: {
      position: google.maps.ControlPosition.LEFT_TOP,
    },
    mapTypeControl: false,
    zoomControl: false,
    cameraControl:false,
    scrollwheel: true,
    mapId: 'cb5fe97220ce0418469f7b27',
  };
  center: google.maps.LatLngLiteral = { lat: 19.041912618713496, lng: 72.86517041341406 };
  zoom = 12;
  
  time: number = DateTime.local().hour;
  minHour = 0;
  maxHour = 24;
  hourMarks: { [key: number]: string } = {};
  aqiData: any[] = [];
  
  // Heatmap properties
  private map: google.maps.Map | null = null;
  private circle: google.maps.Circle | null = null;
  private marker: any = null;
  private markerContent: HTMLElement | null = null;
  private readonly deviceLocation: google.maps.LatLngLiteral = { 
    lat: 19.041912618713496, 
    lng: 72.86517041341406 
  };
  
  // Current AQI value for display
  currentAqi: number | null = null;
  currentAqiCategory: string = '';
  
  constructor(protected feUtil: FeUtil, private beUtil: BeUtil) {
    for (let i = 0; i <= 24; i++) {
      const hourLabel = i < 10 ? `0${i}:00` : `${i}:00`;
      this.hourMarks[i] = hourLabel;
    }
  }

  ngOnInit(): void {
    this.getAqiData();
  }

  onMapReady(map: google.maps.Map): void {
    this.map = map;
    this.updateHeatmap();
  }

  getAqiData() {
    this.beUtil.deviceSvc.getAqiData({
      deviceeui: '70b3d517001977cd',
      start: DateTime.utc().startOf('day').toISO(),
      end: DateTime.utc().endOf('day').toISO()
    }).pipe(untilDestroyed(this), finalize(() => this.loader.aqiLoader = false)).subscribe({
      next: ({ status, data }) => {
        if (status !== 200) return;
        if (!data) return;
        const aqiData = Object.entries(data)
        .flatMap(([deviceeui, deviceData]) =>
          (deviceData && typeof deviceData === 'object' && !Array.isArray(deviceData))
            ? Object.entries(deviceData as { [timestamp: string]: any }).map(([timestamp, val]) => ({
                deviceeui,
                timestamp : DateTime.fromISO(timestamp).toLocal().toISO(),
                ...(val as Record<string, unknown>)
              }))
            : []
        );
        this.aqiData = (aqiData || [])?.sort((a: any, b: any) => DateTime.fromISO(a.timestamp).toMillis() - DateTime.fromISO(b.timestamp).toMillis());
        this.updateHeatmap();
      }
    });
  }

  getFilteredAqiDataByHour(): any[] {
    if (!this.aqiData || this.aqiData.length === 0) return [];
    return this.aqiData.filter(item => {
      if (!item.timestamp) return false;
      const timestamp = DateTime.fromISO(item.timestamp);
      if (!timestamp.isValid) return false;
      const hour = timestamp.hour;
      return hour === this.time;
    });
  }

  getValidAqiValues(data: any[]): number[] {
    return data
      .map(item => item.aqi)
      .filter(aqi => aqi != null && !isNaN(aqi) && typeof aqi === 'number' && aqi >= 0);
  }

  aqiRanges = [
    { label: 'Good', min: 0, max: 50, color: '#2ecc71' },
    { label: 'Moderate', min: 51, max: 100, color: '#f1c40f' },
    { label: 'Poor', min: 101, max: 150, color: '#e67e22' },
    { label: 'Unhealthy', min: 151, max: 200, color: '#e74c3c' },
    { label: 'Severe', min: 201, max: 300, color: '#9b59b6' },
    { label: 'Hazardous', min: 301, max: 500, color: '#c0392b' },
  ];

  getAqiColor(aqi: number): { fillColor: string; strokeColor: string; opacity: number } {
    let range = this.aqiRanges.find(r => aqi >= r.min && aqi <= r.max);
    if (!range) {
      range = this.aqiRanges[this.aqiRanges.length - 1];
    }
    let strokeColor = range.color;
    let opacity = 0.4;
    switch (range.label) {
      case 'Good':
        strokeColor = '#27ae60';
        opacity = 0.4;
        break;
      case 'Moderate':
        strokeColor = '#f39c12';
        opacity = 0.4;
        break;
      case 'Poor':
        strokeColor = '#d35400';
        opacity = 0.5;
        break;
      case 'Unhealthy':
        strokeColor = '#c0392b';
        opacity = 0.6;
        break;
      case 'Severe':
        strokeColor = '#8e44ad';
        opacity = 0.7;
        break;
      case 'Hazardous':
        strokeColor = '#7f2222';
        opacity = 0.8;
        break;
    }
    return {
      fillColor: range.color,
      strokeColor,
      opacity
    };
  }

  getAqiCategory(aqi: number): string {
    const found = this.aqiRanges.find(range => aqi >= range.min && aqi <= range.max);
    return found ? found.label : '';
  }

  createMarkerElement(aqi: number, category: string): HTMLElement {
    const colors = this.getAqiColor(aqi);
    const element = document.createElement('div');
    element.style.cssText = `
      background-color: ${colors.fillColor};
      color: white;
      border: 3px solid ${colors.strokeColor};
      border-radius: 50%;
      width: 80px;
      height: 80px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      font-weight: bold;
      text-align: center;
      padding: 8px;
      filter: blur(3px);
      opacity: 0.8;
    `;

    const aqiValue = document.createElement('div');
    aqiValue.textContent = Math.round(aqi).toString();
    aqiValue.style.cssText = 'font-size: 24px; line-height: 1; margin-bottom: 4px;';

    const categoryLabel = document.createElement('div');
    categoryLabel.textContent = category;
    categoryLabel.style.cssText = 'font-size: 10px; line-height: 1; opacity: 0.9; text-transform: uppercase;';

    element.appendChild(aqiValue);
    element.appendChild(categoryLabel);

    return element;
  }

  updateHeatmap(): void {
    if (!this.map) return;
    const filteredData = this.getFilteredAqiDataByHour();
    const validAqiValues = this.getValidAqiValues(filteredData);
    
    // If no valid AQI data for selected hour, clear everything
    if (validAqiValues.length === 0) {
      if (this.circle) {
        this.circle.setMap(null);
        this.circle = null;
      }
      if (this.marker) {
        if (this.marker.setMap) {
          this.marker.setMap(null);
        } else if (this.marker.map !== undefined) {
          this.marker.map = null;
        }
        this.marker = null;
      }
      this.markerContent = null;
      this.currentAqi = null;
      this.currentAqiCategory = '';
      return;
    }

    // Calculate average only from valid AQI values
    const avgAqi = validAqiValues.reduce((sum, aqi) => sum + aqi, 0) / validAqiValues.length;
    this.currentAqi = avgAqi;
    this.currentAqiCategory = this.getAqiCategory(avgAqi);
    const colors = this.getAqiColor(avgAqi);

    if (!this.circle) {
      this.circle = new google.maps.Circle({
        center: this.deviceLocation,
        radius: 400,
        map: this.map,
        fillColor: colors.fillColor,
        fillOpacity: colors.opacity,
        strokeColor: colors.strokeColor,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        zIndex: 1
      });
    } else {
      this.circle.setOptions({
        fillColor: colors.fillColor,
        fillOpacity: colors.opacity,
        strokeColor: colors.strokeColor
      });
    }

    if (!this.markerContent) {
      this.markerContent = this.createMarkerElement(avgAqi, this.currentAqiCategory);
    } else {
      const colors = this.getAqiColor(avgAqi);
      this.markerContent.style.backgroundColor = colors.fillColor;
      this.markerContent.style.borderColor = colors.strokeColor;
      this.markerContent.style.filter = 'blur(3px)';
      this.markerContent.style.opacity = '0.8';
      const aqiValue = this.markerContent.querySelector('div:first-child');
      const categoryLabel = this.markerContent.querySelector('div:last-child');
      if (aqiValue) aqiValue.textContent = Math.round(avgAqi).toString();
      if (categoryLabel) categoryLabel.textContent = this.currentAqiCategory;
    }

    if (!this.marker) {
      if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
        this.marker = new google.maps.marker.AdvancedMarkerElement({
          map: this.map,
          position: this.deviceLocation,
          content: this.markerContent,
          zIndex: 2
        });
      } else {
        const marker = new google.maps.Marker({
          map: this.map,
          position: this.deviceLocation,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="80" height="80" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <filter id="blur">
                    <feGaussianBlur in="SourceGraphic" stdDeviation="3"/>
                  </filter>
                </defs>
                <circle cx="40" cy="40" r="35" fill="${colors.fillColor}" stroke="${colors.strokeColor}" stroke-width="3" filter="url(#blur)" opacity="0.8"/>
                <text x="40" y="35" font-size="18" font-weight="bold" fill="white" text-anchor="middle">${Math.round(avgAqi)}</text>
                <text x="40" y="55" font-size="10" fill="white" text-anchor="middle" opacity="0.9">${this.currentAqiCategory.toUpperCase()}</text>
              </svg>
            `),
            scaledSize: new google.maps.Size(80, 80),
            anchor: new google.maps.Point(40, 40)
          },
          zIndex: 2
        });
        this.marker = marker;
      }
    } else {
      // Update existing marker
      if (this.marker.content !== undefined) {
        // AdvancedMarkerElement
        this.marker.content = this.markerContent;
      } else if (this.marker.setIcon) {
        // Standard Marker - update icon
        this.marker.setIcon({
          url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
            <svg width="80" height="80" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <filter id="blur">
                  <feGaussianBlur in="SourceGraphic" stdDeviation="3"/>
                </filter>
              </defs>
              <circle cx="40" cy="40" r="35" fill="${colors.fillColor}" stroke="${colors.strokeColor}" stroke-width="3" filter="url(#blur)" opacity="0.8"/>
              <text x="40" y="35" font-size="18" font-weight="bold" fill="white" text-anchor="middle">${Math.round(avgAqi)}</text>
              <text x="40" y="55" font-size="10" fill="white" text-anchor="middle" opacity="0.9">${this.currentAqiCategory.toUpperCase()}</text>
            </svg>
          `),
          scaledSize: new google.maps.Size(80, 80),
          anchor: new google.maps.Point(40, 40)
        });
      }
    }
  }

  onTimeRangeChange(value: NzSliderValue): void {
    this.time = value as number;
    this.updateHeatmap();
  }
}